import { lazy, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

// project import
import MainLayout from 'layout/MainLayout';
import Loadable from 'components/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';

// routes
import usePropertyLevelRoutes from './PropertyLevelRoutes';
import useCompanyLevelRoutes from './CompanyLevelRoutes';
import useBuildingLevelRoutes from './BuildingLevelRoutes';
import useUnitLevelRoutes from './UnitLevelRoutes';

// route interface
import { Navigate, RouteObject } from 'react-router';

// hooks
import useDynamicConfig from 'hooks/useDynamicConfig';
import useEnums from 'hooks/useEnum';
import useSkipCompanyQuery from 'hooks/useSkipCompanyQuery';
import useAuth from 'hooks/useAuth';
import useUserRole from 'hooks/useUserRole';
import useCustomPromptsAccess from 'hooks/useCustomPromptsAccess';

// store
import { useGetCompanyQuery } from 'store/reducers/api/company';

const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));

const OverViewPage = Loadable(lazy(() => import('pages/overview')));
const OverviewHistoryPage = Loadable(lazy(() => import('pages/overview/history')));
const OverviewHistoryRangePage = Loadable(lazy(() => import('pages/overview/history-range')));
const CustomPromptsPage = Loadable(lazy(() => import('pages/overview/custom-prompts')));
const CustomPromptPage = Loadable(lazy(() => import('pages/overview/custom-prompts/prompt')));
const CustomPromptRefinePage = Loadable(lazy(() => import('pages/overview/custom-prompts/refine')));
const CompanyOverViewPage = Loadable(lazy(() => import('pages/companies/admin')));
const PropertyOverViewPage = Loadable(lazy(() => import('pages/properties/admin')));

// main pages
const CompaniesPage = Loadable(lazy(() => import('pages/companies')));
const PropertiesPage = Loadable(lazy(() => import('pages/properties')));
const BuildingsPage = Loadable(lazy(() => import('pages/buildings')));
const ExternalAccountsPage = Loadable(lazy(() => import('pages/external-accounts')));
const AlertsPage = Loadable(lazy(() => import('pages/alerts')));
const GatewaysPage = Loadable(lazy(() => import('pages/gateways')));
const UnassignedSubmissionsPage = Loadable(lazy(() => import('pages/submissions/unassigned')));
const KeepAlivePage = Loadable(lazy(() => import('pages/submissions/keep-alive')));
const ParsedOldPage = Loadable(lazy(() => import('pages/submissions/parsed-old')));
const ReportsPage = Loadable(lazy(() => import('pages/reports')));
const UsersPage = Loadable(lazy(() => import('pages/users')));
const EventsPage = Loadable(lazy(() => import('pages/events')));
const OperationsPage = Loadable(lazy(() => import('pages/operations')));
const ProfilePage = Loadable(lazy(() => import('pages/profile')));
const TestingPage = Loadable(lazy(() => import('pages/micrometers/testing')));
const AssignmentPage = Loadable(lazy(() => import('pages/micrometers/assignment')));
const ConfigPage = Loadable(lazy(() => import('pages/micrometers/config')));
const DefaultsPage = Loadable(lazy(() => import('pages/micrometers/defaults')));
const AppliancesPage = Loadable(lazy(() => import('pages/appliances')));
const AssignedMicrometersPage = Loadable(lazy(() => import('pages/micrometers/assigned')));
const UnassignedMicrometersPage = Loadable(lazy(() => import('pages/micrometers/unassigned')));
const ExcludedMicrometersPage = Loadable(lazy(() => import('pages/micrometers/excluded')));
const DownlinkPage = Loadable(lazy(() => import('pages/micrometers/downlink')));
const EmailLinks = Loadable(lazy(() => import('pages/email-links/EmailLinks')));
const ConfirmationPage = Loadable(lazy(() => import('pages/confirmation/Confirmation')));
const InstallationPage = Loadable(lazy(() => import('pages/companies/installation')));
const RealpageAdminPage = Loadable(lazy(() => import('pages/admin/realpage')));
const UtilityCompaniesPage = Loadable(lazy(() => import('pages/utility-companies')));
const PropertyOwnersPage = Loadable(lazy(() => import('pages/property-owners')));
// ==============================|| MAIN ROUTING ||============================== //

const useMainRoute = () => {
  const { activeRole } = useAuth();
  const { hasSystemRole, hasCompanyRole, hasPropertyRole } = useUserRole();
  const { UserRolesEnum } = useEnums();
  const { SYSTEM_ROLES, COMPANY_ROLES, PROPERTY_ROLES } = useDynamicConfig();
  const { enabled: customPromptsEnabled, allowedRoles: customPromptsRoles } = useCustomPromptsAccess();
  const skipCompanyQuery = useSkipCompanyQuery(activeRole);
  const {
    data: userCompany,
    isLoading: loading,
    error
  } = useGetCompanyQuery(activeRole?.company_id!, {
    skip: skipCompanyQuery
  });

  // Routes
  const propertyLevelRoutes = usePropertyLevelRoutes();
  const companyLevelRoutes = useCompanyLevelRoutes();
  const buildingLevelRoutes = useBuildingLevelRoutes();
  const unitLevelRoutes = useUnitLevelRoutes();

  const { dashboardPath, dashboardPage } = useMemo(() => {
    if (!activeRole) {
      return { dashboardPath: '', dashboardPage: null };
    }

    return {
      dashboardPath: hasSystemRole ? 'overview' : 'dashboard',
      dashboardPage: hasSystemRole ? (
        <OverViewPage />
      ) : hasCompanyRole ? (
        <CompanyOverViewPage />
      ) : hasPropertyRole ? (
        <PropertyOverViewPage />
      ) : null
    };
  }, [activeRole, hasSystemRole, hasCompanyRole, hasPropertyRole]);

  const mainRoutes: RouteObject = useMemo(() => {
    const gatewaysRoute = {
      path: 'gateways',
      element: <GatewaysPage />,
      handle: {
        crumb: () => <FormattedMessage id="gateways" />,
        allowedRoles: [...SYSTEM_ROLES, ...COMPANY_ROLES, ...PROPERTY_ROLES]
      }
    };

    const isBillingCompany = userCompany?.company_type_id === 3;
    return {
      path: '/',
      errorElement: <MaintenanceError500 />,
      children: [
        {
          path: '/',
          element: (
            <AuthGuard>
              <MainLayout />
            </AuthGuard>
          ),
          handle: { crumb: () => <FormattedMessage id="home" /> },
          children: [
            ...(isBillingCompany
              ? []
              : [
                  {
                    path: dashboardPath,
                    element: dashboardPage,
                    handle: {
                      crumb: () => <FormattedMessage id={dashboardPath} />,
                      allowedRoles: [...SYSTEM_ROLES, ...COMPANY_ROLES, ...PROPERTY_ROLES]
                    }
                  },
                  {
                    path: 'overview/history',
                    element: <OverviewHistoryPage />,
                    handle: {
                      crumb: () => 'Overview History',
                      allowedRoles: SYSTEM_ROLES
                    }
                  },
                  {
                    path: 'overview/history/range',
                    element: <OverviewHistoryRangePage />,
                    handle: {
                      crumb: () => 'Overview History (Range)',
                      allowedRoles: SYSTEM_ROLES
                    }
                  },
                  ...(customPromptsEnabled
                    ? [
                        {
                          path: 'overview/custom-prompts',
                          element: <CustomPromptsPage />,
                          handle: {
                            crumb: () => 'Custom prompt emails',
                            allowedRoles: customPromptsRoles
                          }
                        },
                        {
                          path: 'overview/custom-prompts/:id',
                          element: <CustomPromptPage />,
                          handle: {
                            crumb: () => 'Prompt',
                            allowedRoles: customPromptsRoles
                          }
                        },
                        {
                          path: 'overview/custom-prompts/:id/refine/:sessionId',
                          element: <CustomPromptRefinePage />,
                          handle: {
                            crumb: () => 'Refine',
                            allowedRoles: customPromptsRoles
                          }
                        }
                      ]
                    : [])
                ]),
            {
              path: 'companies',
              element: hasSystemRole ? (
                <CompaniesPage />
              ) : hasCompanyRole ? (
                <Navigate to={['/companies', activeRole?.company_id].filter((x) => x).join('/')} replace />
              ) : hasPropertyRole ? (
                <Navigate
                  to={['/companies', activeRole?.company_id, 'properties', activeRole?.property_id].filter((x) => x).join('/')}
                  replace
                />
              ) : null,
              handle: {
                crumb: () => <FormattedMessage id="companies" />,
                allowedRoles: [...SYSTEM_ROLES, ...COMPANY_ROLES]
              }
            },
            {
              path: 'properties',
              element: <PropertiesPage />,
              handle: {
                crumb: () => <FormattedMessage id="properties" />,
                allowedRoles: SYSTEM_ROLES
              }
            },
            {
              path: 'buildings',
              element: <BuildingsPage />,
              handle: {
                crumb: () => <FormattedMessage id="buildings" />,
                allowedRoles: SYSTEM_ROLES
              }
            },
            {
              path: 'installation',
              element: <InstallationPage />,
              handle: {
                crumb: () => [userCompany?.name, 'Installation'].join(' / '),
                title: () => <FormattedMessage id="installation" />,
                allowedRoles: [UserRolesEnum.COMPANY_INSTALLER]
              }
            },
            {
              path: 'flume-accounts',
              element: <ExternalAccountsPage />,
              handle: {
                crumb: () => <FormattedMessage id="flume-accounts" />,
                allowedRoles: SYSTEM_ROLES
              }
            },
            {
              path: 'admin/realpage',
              element: <RealpageAdminPage />,
              handle: {
                crumb: () => <FormattedMessage id="realpage on boarding" />,
                allowedRoles: SYSTEM_ROLES
              }
            },
            {
              path: 'alerts',
              element: <AlertsPage />,
              handle: {
                crumb: () => <FormattedMessage id="alerts" />,
                allowedRoles: SYSTEM_ROLES
              }
            },
            companyLevelRoutes,
            ...(isBillingCompany ? [] : [propertyLevelRoutes, buildingLevelRoutes, unitLevelRoutes, gatewaysRoute]),
            {
              path: 'submissions',
              handle: {
                collapse: true,
                crumb: () => <FormattedMessage id="submissions" />
              },
              children: [
                {
                  path: 'unassigned',
                  element: <UnassignedSubmissionsPage />,
                  handle: {
                    crumb: () => <FormattedMessage id="unassigned" />,
                    allowedRoles: SYSTEM_ROLES
                  }
                },
                {
                  path: 'keep-alive',
                  element: <KeepAlivePage />,
                  handle: {
                    crumb: () => <FormattedMessage id="keep-alive" />,
                    allowedRoles: SYSTEM_ROLES
                  }
                },
                {
                  path: 'parsed-old',
                  element: <ParsedOldPage />,
                  handle: {
                    crumb: () => <FormattedMessage id="parsed-old" />,
                    allowedRoles: SYSTEM_ROLES
                  }
                }
              ]
            },
            {
              path: 'reports',
              element: <ReportsPage />,
              handle: {
                crumb: () => <FormattedMessage id="reports" />,
                allowedRoles: [...SYSTEM_ROLES, ...COMPANY_ROLES]
              }
            },
            ...(isBillingCompany
              ? []
              : [
                  {
                    path: 'users',
                    element: <UsersPage />,
                    handle: {
                      crumb: () => <FormattedMessage id="users" />,
                      allowedRoles: [...SYSTEM_ROLES, ...COMPANY_ROLES, ...PROPERTY_ROLES]
                    }
                  }
                ]),
            {
              path: 'utility-companies',
              element: <UtilityCompaniesPage />,
              handle: {
                crumb: () => <FormattedMessage id="utility-companies" />,
                allowedRoles: SYSTEM_ROLES
              }
            },
            {
              path: 'property-owners',
              element: <PropertyOwnersPage />,
              handle: {
                crumb: () => <FormattedMessage id="property-owners" />,
                allowedRoles: SYSTEM_ROLES
              }
            },
            {
              path: 'events',
              element: <EventsPage />,
              handle: {
                crumb: () => <FormattedMessage id="events" />,
                allowedRoles: SYSTEM_ROLES
              }
            },
            {
              path: 'operations',
              element: <OperationsPage />,
              handle: {
                crumb: () => <FormattedMessage id="operations" />,
                allowedRoles: SYSTEM_ROLES
              }
            },
            {
              path: 'micrometers',
              handle: {
                collapse: true,
                crumb: () => <FormattedMessage id="micrometers" />
              },
              children: [
                {
                  path: 'assigned',
                  element: <AssignedMicrometersPage />,
                  handle: {
                    crumb: () => <FormattedMessage id="assigned" />,
                    allowedRoles: SYSTEM_ROLES
                  }
                },
                {
                  path: 'unassigned',
                  element: <UnassignedMicrometersPage />,
                  handle: {
                    crumb: () => <FormattedMessage id="unassigned" />,
                    allowedRoles: SYSTEM_ROLES
                  }
                },
                {
                  path: 'excluded',
                  element: <ExcludedMicrometersPage />,
                  handle: {
                    crumb: () => <FormattedMessage id="excluded" />,
                    allowedRoles: SYSTEM_ROLES
                  }
                },
                {
                  path: 'assignment',
                  element: <AssignmentPage />,
                  handle: {
                    crumb: () => <FormattedMessage id="assignment" />,
                    allowedRoles: userCompany?.allow_assignment ? [...SYSTEM_ROLES, UserRolesEnum.COMPANY_ADMIN] : SYSTEM_ROLES
                  }
                },
                {
                  path: 'config',
                  element: <ConfigPage />,
                  handle: {
                    crumb: () => <FormattedMessage id="config" />,
                    allowedRoles: SYSTEM_ROLES
                  }
                },
                {
                  path: 'defaults',
                  element: <DefaultsPage />,
                  handle: {
                    crumb: () => <FormattedMessage id="defaults" />,
                    allowedRoles: SYSTEM_ROLES
                  }
                },
                {
                  path: 'testing',
                  element: <TestingPage />,
                  handle: {
                    crumb: () => <FormattedMessage id="testing" />,
                    allowedRoles: [...SYSTEM_ROLES, UserRolesEnum.SENSOR_TESTER]
                  }
                },
                {
                  path: 'downlink',
                  element: <DownlinkPage />,
                  handle: {
                    crumb: () => <FormattedMessage id="downlink" />,
                    allowedRoles: SYSTEM_ROLES
                  }
                }
              ]
            },
            {
              path: 'appliances',
              element: <AppliancesPage />,
              handle: {
                crumb: () => <FormattedMessage id="fixtures" />,
                allowedRoles: SYSTEM_ROLES
              }
            },
            {
              path: 'settings',
              element: <ProfilePage />,
              handle: {
                crumb: () => <FormattedMessage id="settings" />,
                allowedRoles: [...SYSTEM_ROLES, ...COMPANY_ROLES, ...PROPERTY_ROLES, UserRolesEnum.NOTIFICATION_CONTACT]
              }
            }
          ]
        },
        {
          path: '/email-links/:status',
          element: <EmailLinks />
        },
        {
          path: '/confirmation/:notification_id/:status/:token',
          element: <ConfirmationPage />
        }
      ]
    };
  }, [
    SYSTEM_ROLES,
    COMPANY_ROLES,
    PROPERTY_ROLES,
    customPromptsRoles,
    customPromptsEnabled,
    propertyLevelRoutes,
    companyLevelRoutes,
    buildingLevelRoutes,
    unitLevelRoutes,
    UserRolesEnum,
    dashboardPage,
    dashboardPath,
    userCompany,
    activeRole,
    hasSystemRole,
    hasCompanyRole,
    hasPropertyRole
  ]);

  if (loading || error) return { mainRoutes: {}, loading };

  return { mainRoutes, loading };
};

export default useMainRoute;
